import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { useWebhookActivityQuery } from "@/modules/facebook/presentation/api/queries";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import type { WebhookActivityItem, WebhookActivityStats } from "../api/types";

const JOB_STATUS_LABEL: Record<string, string> = {
  queued: "ໃນຄິວ",
  processing: "ກຳລັງປະມວນຜົນ",
  completed: "ປະມວນຜົນແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  dead: "ຍົກເລີກ",
};

function jobStatusVariant(
  status: string | null,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "completed") return "default";
  if (status === "processing" || status === "queued") return "secondary";
  if (status === "failed" || status === "dead") return "destructive";
  return "outline";
}

function StatsRow({ stats }: { stats: WebhookActivityStats }) {
  const completed =
    stats.byJobStatus.find((s) => s.status === "completed")?.count ?? 0;
  const failed =
    (stats.byJobStatus.find((s) => s.status === "failed")?.count ?? 0) +
    (stats.byJobStatus.find((s) => s.status === "dead")?.count ?? 0);
  const inflight =
    (stats.byJobStatus.find((s) => s.status === "queued")?.count ?? 0) +
    (stats.byJobStatus.find((s) => s.status === "processing")?.count ?? 0);

  const feed =
    stats.byEventType.find((e) => e.eventType === "feed")?.count ?? 0;
  const messages =
    stats.byEventType.find((e) => e.eventType === "messages")?.count ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Badge variant="outline" className="gap-1">
        <Activity className="size-3" />
        24 ຊມ. {stats.total24h}
      </Badge>
      {feed > 0 ? <Badge variant="outline">comment {feed}</Badge> : null}
      {messages > 0 ? (
        <Badge variant="outline">message {messages}</Badge>
      ) : null}
      {completed > 0 ? (
        <Badge variant="default" className="gap-1">
          <CheckCircle2 className="size-3" />
          ປະມວນຜົນແລ້ວ {completed}
        </Badge>
      ) : null}
      {inflight > 0 ? (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="size-3 animate-spin" />
          ກຳລັງປະມວນຜົນ {inflight}
        </Badge>
      ) : null}
      {failed > 0 ? (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="size-3" />
          ລົ້ມເຫຼວ {failed}
        </Badge>
      ) : null}
    </div>
  );
}

function EventRow({ item }: { item: WebhookActivityItem }) {
  const jobLabel = item.jobStatus
    ? (JOB_STATUS_LABEL[item.jobStatus] ?? item.jobStatus)
    : "ບໍ່ມີງານ";

  return (
    <tr className="border-b last:border-0">
      <td className="whitespace-nowrap px-3 py-2 text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Clock className="size-3" />
          {formatDateTimeLocal(item.receivedAt)}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-sm">{item.eventType}</span>
          {item.facebookChangeField ? (
            <span className="text-muted-foreground text-xs">
              {item.facebookChangeField}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        {item.facebookPageId ? (
          <span className="font-mono">{item.facebookPageId}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge
          variant={item.signatureValid ? "outline" : "secondary"}
          className="gap-1"
        >
          {item.signatureValid ? (
            <ShieldCheck className="size-3" />
          ) : (
            <ShieldAlert className="size-3" />
          )}
          {item.signatureValid ? "valid" : "skipped"}
        </Badge>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <Badge
            variant={jobStatusVariant(item.jobStatus)}
            className="w-fit text-xs"
          >
            {jobLabel}
            {item.jobAttempts && item.jobAttempts > 1
              ? ` · ${item.jobAttempts}x`
              : ""}
          </Badge>
          {item.jobLastError ? (
            <span
              className="line-clamp-2 max-w-[28ch] text-destructive text-xs"
              title={item.jobLastError}
            >
              {item.jobLastError}
            </span>
          ) : null}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-muted-foreground text-xs">
        {item.eventId.slice(0, 10)}…
      </td>
    </tr>
  );
}

export function WebhookActivityPanel() {
  const [open, setOpen] = useState(false);
  const { data, isFetching, refetch, error } = useWebhookActivityQuery({
    limit: 50,
    enabled: true,
  });

  const events = data?.events ?? [];
  const stats = data?.stats ?? {
    total24h: 0,
    byEventType: [],
    byJobStatus: [],
  };

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
        <div className="flex min-w-0 items-center gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="size-4" />
            Webhook activity
            {isFetching ? (
              <Loader2 className="size-3 animate-spin text-muted-foreground" />
            ) : null}
          </CardTitle>
          <div className="hidden sm:block">
            <StatsRow stats={stats} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            ໂຫລດໃໝ່
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            {open ? "ປິດ" : "ເປີດ"}
          </Button>
        </div>
      </CardHeader>
      <div className="border-t px-6 pt-2 pb-3 sm:hidden">
        <StatsRow stats={stats} />
      </div>
      {open ? (
        <CardContent className="pt-0">
          {error ? (
            <p className="rounded border border-destructive/50 bg-destructive/5 p-3 text-destructive text-sm">
              ດຶງຂໍ້ມູນ webhook ບໍ່ສຳເລັດ
            </p>
          ) : events.length === 0 ? (
            <p className="rounded border border-dashed p-6 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີ event — ໃຫ້ Facebook ສົ່ງ test event ຫຼື comment ໃນ post ທີ່
              subscribe webhook
            </p>
          ) : (
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-muted-foreground text-xs">
                  <tr>
                    <th className="px-3 py-2 font-medium">ເວລາ</th>
                    <th className="px-3 py-2 font-medium">ປະເພດ</th>
                    <th className="px-3 py-2 font-medium">Page ID</th>
                    <th className="px-3 py-2 font-medium">ລາຍເຊັນ</th>
                    <th className="px-3 py-2 font-medium">ສະຖານະງານ</th>
                    <th className="px-3 py-2 font-medium">Event ID</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((item) => (
                    <EventRow key={item.eventId} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-muted-foreground text-xs">
            ໂຫລດໃໝ່ອັດຕະໂນມັດທຸກ 10 ວິນາທີ · ສະແດງ 50 event ລ່າສຸດ
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            ສະຖານະ “ປະມວນຜົນແລ້ວ” ໝາຍເຖິງ webhook ຖືກປະມວນຜົນແລ້ວ; ກວດສະຖານະສົ່ງ
            Messenger ໄດ້ໃນໜ້າຄອມເມັນ.
          </p>
        </CardContent>
      ) : null}
    </Card>
  );
}
